import { useCallback, useEffect, useRef, useState } from 'react';
import Section from '../shared/Section';
import Btn from '../shared/Btn';
import Icon from '../shared/Icon';
import Modal from '../shared/Modal';
import {
  uploadDocument, deleteClaimDocument, listClaimDocuments,
} from '../../../api/claimsClient';
import { fetchOptumDocument, listOptumClaimDocuments } from '../../../api/optumApi';
import { PdfViewerModal } from '../../documents/PdfViewerModal';

// Show the loading modal only if the fetch takes longer than this — otherwise
// fast network round-trips would flash the modal in and out.
const LOADING_MODAL_DELAY_MS = 250;

// Mirrors claims.models.ClaimDocumentType — keep in sync if backend adds enum values.
const DOC_TYPE_LABELS = {
  claim_form: 'Claim Form',
  eob: 'EOB',
  denial_letter: 'Denial Letter',
  medical_record: 'Medical Record',
  insurance_card: 'Insurance Card',
  prescription: 'Prescription',
  lab_result: 'Lab Result',
  referral: 'Referral',
  prior_auth: 'Prior Authorization',
  appeal_letter: 'Appeal Letter',
  supporting_docs: 'Supporting Documents',
  lmn: 'Letter of Medical Necessity',
  clinical_notes: 'Clinical Notes',
  other: 'Other',
};

/** @param {string|undefined} type */
function prettyDocType(type) {
  if (!type) return 'Other';
  return /** @type {Record<string, string>} */ (DOC_TYPE_LABELS)[type] || type;
}

/** @param {number|null|undefined} bytes */
function formatBytes(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DOC_TYPE_ICONS = {
  lmn: 'file-signature',
  eob: 'receipt',
  denial_letter: 'file-x',
  medical_record: 'file-heart',
  insurance_card: 'credit-card',
  prescription: 'pill',
  lab_result: 'flask-conical',
  referral: 'file-symlink',
  prior_auth: 'shield-check',
  appeal_letter: 'mail',
  supporting_docs: 'paperclip',
  clinical_notes: 'notebook-pen',
  claim_form: 'file-text',
  other: 'file',
};

/**
 * @param {{
 *   claim: any,
 *   editable: boolean,
 *   onAlert: (msg: string, sev?: string) => void,
 *   onGenerateLMN: () => void,
 *   onOpenLetter?: (letterId: string) => void,
 *   lmnLetters: Array<{ letterId: string, status: string }>,
 *   onDocumentsChanged: () => void,
 *   documentsRefreshKey?: number,
 *   onDeleteLMN?: (letterId: string) => void,
 * }} props
 */
export default function DocumentsSection({
  claim, editable, onAlert, onGenerateLMN, onOpenLetter, lmnLetters,
  onDocumentsChanged, documentsRefreshKey = 0, onDeleteLMN,
}) {
  /** @type {[Array<{tempId: string, name: string, size: number, status: string}>, any]} */
  const [uploads, setUploads] = useState(/** @type {any} */ ([]));
  /** @type {[any[], any]} */
  const [supporting, setSupporting] = useState(/** @type {any[]} */ ([]));
  /** @type {[any[], any]} */
  const [optumDocs, setOptumDocs] = useState(/** @type {any[]} */ ([]));
  const [docsLoading, setDocsLoading] = useState(false);
  const [optumBusyId, setOptumBusyId] = useState(/** @type {string|null} */ (null));
  const [viewer, setViewer] = useState({
    open: false,
    documentType: /** @type {string|null} */ (null),
    documentId: /** @type {string|null} */ (null),
    documentLabel: '',
  });
  /** @type {any} */
  const fileInputRef = useRef(null);
  const onAlertRef = useRef(onAlert);

  useEffect(() => {
    onAlertRef.current = onAlert;
  }, [onAlert]);

  // The Claim read payload (claims/mappers.py::claim_to_read_schema) does NOT
  // include supporting_documents — that list lives behind GET /claims/{id}/documents/.
  // Fetch it explicitly on mount and whenever onDocumentsChanged fires
  // (post-upload, post-delete, post-LMN-attach).
  // While the fetch is in flight, surface a small loading modal so the user
  // knows the list is being retrieved (only after a small delay so quick
  // round-trips don't flash the modal in and out).
  const refreshDocs = useCallback(async () => {
    if (!claim?.id) return;
    let didFinish = false;
    const showTimer = setTimeout(() => {
      if (!didFinish) setDocsLoading(true);
    }, LOADING_MODAL_DELAY_MS);
    try {
      const [claimDocsResult, optumDocsResult] = await Promise.allSettled([
        listClaimDocuments(claim.id),
        listOptumClaimDocuments(claim.id),
      ]);
      if (claimDocsResult.status === 'fulfilled') {
        const list = claimDocsResult.value;
        setSupporting(Array.isArray(list) ? list : []);
      } else {
        throw claimDocsResult.reason;
      }
      if (optumDocsResult.status === 'fulfilled') {
        const list = optumDocsResult.value;
        setOptumDocs(Array.isArray(list) ? list : []);
      } else {
        setOptumDocs([]);
      }
    } catch (/** @type {any} */ e) {
      onAlertRef.current?.(`Failed to load documents: ${e?.message || 'error'}`, 'error');
    } finally {
      didFinish = true;
      clearTimeout(showTimer);
      setDocsLoading(false);
    }
  }, [claim?.id]);

  useEffect(() => { refreshDocs(); }, [refreshDocs]);

  // Server-mediated signed-URL view flow (quick task 260518-u54), now rendered
  // inside an in-app PdfViewerModal (quick task 260518-wnv) instead of
  // window.open. The modal owns the requestSignedView lifecycle — including
  // the silent re-issue ~60s before the signed URL expires — so this handler
  // only needs to flip the viewer state.
  const handleViewClick = (documentType, documentId) => {
    if (!documentType || !documentId) {
      onAlertRef.current?.('Cannot open document — missing identifier', 'error');
      return;
    }
    setViewer({
      open: true,
      documentType,
      documentId,
      documentLabel: prettyDocType(documentType),
    });
  };

  // Parent (ClaimFlowPage) bumps documentsRefreshKey after a save, regenerate-
  // terminal, or detach so we re-fetch without prop drilling a callback.
  useEffect(() => {
    if (documentsRefreshKey > 0) refreshDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsRefreshKey]);

  /** @param {FileList|File[]|null|undefined} files */
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const tempId = `up_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const row = { tempId, name: file.name, size: file.size, status: 'uploading' };
      setUploads((/** @type {any[]} */ prev) => [...prev, row]);
      try {
        // Backend's upload_document/ requires a document_type from the
        // ClaimDocumentType enum. Drop-zone uploads have no per-file type
        // selector yet, so default to 'supporting_docs'. Generated artifacts
        // (LMN) flow through their own backend code path with their own type.
        await uploadDocument(claim.id, file, { document_type: 'supporting_docs' });
        setUploads((/** @type {any[]} */ prev) => prev.filter((r) => r.tempId !== tempId));
        await refreshDocs();
        onDocumentsChanged?.();
      } catch (/** @type {any} */ e) {
        setUploads((/** @type {any[]} */ prev) => prev.map((r) => (r.tempId === tempId ? { ...r, status: 'failed' } : r)));
        onAlert?.(`Failed to upload ${file.name}: ${e?.message || 'error'}`, 'error');
      }
    }
  };

  /** @param {string} attachmentId */
  const removeAttachment = async (attachmentId) => {
    try {
      await deleteClaimDocument(claim.id, attachmentId);
      await refreshDocs();
      onDocumentsChanged?.();
    } catch (/** @type {any} */ e) {
      onAlert?.(`Failed to delete: ${e?.message || 'error'}`, 'error');
    }
  };

  /** @param {any} doc */
  const fetchOptum = async (doc) => {
    if (!doc?.id) return;
    setOptumBusyId(doc.id);
    try {
      await fetchOptumDocument(doc.id);
      await refreshDocs();
      onDocumentsChanged?.();
      onAlert?.('Optum document fetch queued.', 'success');
    } catch (/** @type {any} */ e) {
      onAlert?.(`Failed to fetch Optum document: ${e?.message || 'error'}`, 'error');
    } finally {
      setOptumBusyId(null);
    }
  };

  // LMN polling triggers onDocumentsChanged via ClaimFlowPage; refresh in
  // response so newly-attached LMN PDFs surface without a full page reload.
  // Watch lmnLetters length / statuses as a proxy signal.
  const lmnGenerated = (lmnLetters || []).filter((j) => j.status === 'generated').length;
  useEffect(() => {
    if (lmnGenerated > 0) refreshDocs();
  }, [lmnGenerated, refreshDocs]);

  const supportingDocumentIds = new Set(supporting.map((/** @type {any} */ att) => String(att.document_id || '')));
  const visibleOptumDocs = optumDocs.filter((/** @type {any} */ doc) => {
    if (doc.fetch_status !== 'fetched') return true;
    if (!doc.uploaded_document_id) return true;
    return !supportingDocumentIds.has(String(doc.uploaded_document_id));
  });

  return (
    <Section icon="paperclip" title="Documents" subtitle="Letters, EOBs, supporting records">
      {/* Generators */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginBottom: 16 }}>
        <GeneratorCard
          title="Letter of Medical Necessity"
          desc="AI-drafted clinical justification."
          icon="sparkles"
          actionLabel="Generate LMN"
          onAction={onGenerateLMN}
          disabled={!editable}
        />
        <GeneratorCard title="Clinical Summary" desc="Coming soon." icon="file-text" actionLabel="Coming soon" disabled />
        <GeneratorCard title="Prior Authorization" desc="Coming soon." icon="shield-check" actionLabel="Coming soon" disabled />
      </div>

      {/* Drop-zone */}
      {editable && (
        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: '1.5px dashed var(--ab-border-soft)',
            borderRadius: 'var(--ab-radius-md)',
            padding: 24, textAlign: 'center',
            cursor: 'pointer',
            color: 'var(--ab-fg-subtle)',
            marginBottom: 16,
          }}
        >
          <Icon name="upload-cloud" size={20} />
          <div style={{ marginTop: 8, fontSize: 13 }}>Click or drag files here to attach.</div>
          <input
            ref={fileInputRef} type="file" multiple hidden
            onChange={(/** @type {any} */ e) => { handleFiles(e.target.files); e.target.value = ''; }}
          />
        </div>
      )}

      {/* Attached docs list */}
      <div>
        {(lmnLetters || []).map((j) => (
          <div key={j.letterId} style={rowStyle()}>
            <Icon name="loader-circle" size={14} />
            <div style={{ flex: 1 }}>LMN — {j.status}</div>
            {editable && (
              <button
                type="button"
                aria-label="Cancel Letter of Medical Necessity"
                title="Cancel and remove this letter"
                onClick={() => {
                  if (window.confirm('Cancel this Letter of Medical Necessity? This cannot be undone.')) {
                    onDeleteLMN?.(j.letterId);
                  }
                }}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  display: 'inline-flex', padding: 4,
                  color: 'var(--ab-fg-subtle)',
                }}
              >
                <Icon name="trash-2" size={14} />
              </button>
            )}
          </div>
        ))}
        {uploads.map((/** @type {any} */ u) => (
          <div key={u.tempId} style={rowStyle(u.status === 'failed')}>
            <Icon name={u.status === 'attached' ? 'check' : (u.status === 'failed' ? 'circle-x' : 'loader-circle')} size={14} />
            <div style={{ flex: 1 }}>{u.name}</div>
            <span style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>{u.status}</span>
          </div>
        ))}
        {visibleOptumDocs.map((/** @type {any} */ doc) => {
          const filename = doc.file_name || 'Optum document';
          const canonicalType = doc.canonical_document_type || doc.document_type;
          const iconName = /** @type {Record<string, string>} */ (DOC_TYPE_ICONS)[canonicalType] || 'file';
          const canFetch = ['pending', 'error', 'blocked'].includes(doc.fetch_status) && doc.fetch_status !== 'blocked';
          const isBusy = optumBusyId === doc.id;
          return (
            <div key={`optum_${doc.id}`} style={rowStyle(doc.fetch_status === 'error')}>
              <Icon name={doc.fetch_status === 'fetched' ? iconName : 'cloud-download'} size={14} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 'var(--ab-weight-semi)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{filename}</div>
                <div style={{ fontSize: 11, color: 'var(--ab-fg-subtle)' }}>
                  Optum {prettyDocType(canonicalType)} · {doc.fetch_status || 'pending'}
                </div>
              </div>
              {doc.uploaded_document_id && (
                // Quick task 260518-u54: server-mediated signed-URL flow.
                // The raw GCS download_url is no longer serialized; we POST to
                // /api/v1/documents/eob/<uploaded_document_id>/view-url/ instead.
                <button
                  type="button"
                  aria-label={`Open ${filename}`}
                  title="Open document"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewClick('eob', doc.uploaded_document_id);
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--ab-fg-subtle)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    padding: 4,
                  }}
                >
                  <Icon name="external-link" size={14} />
                </button>
              )}
              {editable && canFetch && (
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => fetchOptum(doc)}
                  style={{
                    border: '1px solid var(--ab-border-soft)',
                    background: '#fff',
                    borderRadius: 6,
                    cursor: isBusy ? 'wait' : 'pointer',
                    color: 'var(--ab-fg)',
                    fontSize: 12,
                    fontWeight: 'var(--ab-weight-semi)',
                    padding: '5px 8px',
                  }}
                >
                  {isBusy ? 'Queueing' : 'Fetch'}
                </button>
              )}
            </div>
          );
        })}
        {supporting.map((/** @type {any} */ att) => {
          // Read serializer (claims/serializers/read.py::ClaimSupportingDocumentSerializer)
          // returns flat keys: filename, document_type, file_size, uploaded_at,
          // download_url, attached_at, lmn_letter_id. Don't reach into a nested document object.
          const filename = att.filename || 'document';
          const typeLabel = prettyDocType(att.document_type);
          const iconName = /** @type {Record<string, string>} */ (DOC_TYPE_ICONS)[att.document_type] || 'file';
          const size = formatBytes(att.file_size);
          // LMN row is openable in the editor only when the letter has populated
          // attached_document (the new contract). Legacy LMN rows without the FK
          // remain static (and detachable, falling through the existing path).
          const isOpenableLMN = att.document_type === 'lmn' && !!att.lmn_letter_id;
          return (
            <div
              key={att.id}
              style={{ ...rowStyle(), cursor: isOpenableLMN ? 'pointer' : rowStyle().cursor }}
              onClick={isOpenableLMN ? () => onOpenLetter?.(att.lmn_letter_id) : undefined}
              role={isOpenableLMN ? 'button' : undefined}
              tabIndex={isOpenableLMN ? 0 : undefined}
              onKeyDown={isOpenableLMN ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenLetter?.(att.lmn_letter_id);
                }
              } : undefined}
              aria-label={isOpenableLMN ? `Edit ${filename}` : undefined}
            >
              <Icon name={iconName} size={14} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 'var(--ab-weight-semi)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{filename}</div>
                <div style={{ fontSize: 11, color: 'var(--ab-fg-subtle)' }}>
                  {typeLabel}{size ? ` · ${size}` : ''}
                </div>
              </div>
              {(att.document_type === 'eob' || att.document_type === 'lmn') && att.document_id && (
                // Quick task 260518-u54: server-mediated signed-URL flow.
                // The raw download_url is no longer serialized; we POST to
                // /api/v1/documents/<type>/<id>/view-url/ instead. Only EOB and
                // LMN are wired through view-url in this PR; other supporting
                // doc types stay non-viewable until follow-up scope.
                <button
                  type="button"
                  aria-label={`Open ${filename}`}
                  title="Open document"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewClick(att.document_type, att.document_id);
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--ab-fg-subtle)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    padding: 4,
                  }}
                >
                  <Icon name="external-link" size={14} />
                </button>
              )}
              {editable && isOpenableLMN && (
                <button
                  type="button"
                  aria-label={`Delete ${filename}`}
                  title="Delete Letter of Medical Necessity"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Delete this Letter of Medical Necessity? This cannot be undone.')) {
                      onDeleteLMN?.(att.lmn_letter_id);
                    }
                  }}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    display: 'inline-flex', padding: 4,
                    color: 'var(--ab-fg-subtle)',
                  }}
                >
                  <Icon name="trash-2" size={14} />
                </button>
              )}
              {editable && !isOpenableLMN && (
                <button
                  type="button"
                  aria-label={`Detach ${filename}`}
                  title="Detach from claim"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Detach "${filename}" from this claim?`)) {
                      removeAttachment(att.id);
                    }
                  }}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    display: 'inline-flex', padding: 4,
                    color: 'var(--ab-fg-subtle)',
                  }}
                >
                  <Icon name="trash-2" size={14} />
                </button>
              )}
            </div>
          );
        })}
        {(supporting.length === 0 && visibleOptumDocs.length === 0 && uploads.length === 0 && (lmnLetters || []).length === 0) && (
          <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', padding: 12 }}>
            No documents attached yet.
          </div>
        )}
      </div>
      <Modal open={docsLoading} onClose={() => {}} title="Loading documents" width={320}>
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 4px',
            fontSize: 13,
            color: 'var(--ab-fg)',
          }}
        >
          <Icon name="loader-circle" size={18} />
          <span>Retrieving documents attached to this claim…</span>
        </div>
      </Modal>
      <PdfViewerModal
        open={viewer.open}
        onClose={() => setViewer((s) => ({ ...s, open: false }))}
        documentType={viewer.documentType}
        documentId={viewer.documentId}
        documentLabel={viewer.documentLabel}
      />
    </Section>
  );
}

/**
 * @param {{ title: string, desc: string, icon: string, actionLabel: string, onAction?: () => void, disabled?: boolean }} props
 */
function GeneratorCard({ title, desc, icon, actionLabel, onAction, disabled }) {
  return (
    <div style={{
      border: 'var(--ab-hairline)', borderRadius: 'var(--ab-radius-md)',
      padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name={icon} size={16} />
        <div style={{ fontSize: 14, fontWeight: 'var(--ab-weight-semi)' }}>{title}</div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', flex: 1 }}>{desc}</div>
      <Btn kind="primary" size="sm" disabled={disabled} onClick={onAction}>{actionLabel}</Btn>
    </div>
  );
}

/** @param {boolean} [failed] */
function rowStyle(failed = false) {
  return {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 0', fontSize: 13,
    borderBottom: '1px solid var(--ab-border-hair)',
    color: failed ? 'var(--ab-denied)' : 'var(--ab-fg)',
  };
}
